import "./RepoFilter.css";
import { Button } from "./Button";
import { Input, Select } from "./Field";
import { tx } from "../i18n";

/* ══ git 저장소 › 건 저장소 거르개(t-in-port-work-repos) ══
   pr.b3x.app 리포지토리 화면의 「팀 탭 · 검색 · 거르개」를 b3x-in 이 **가진 값**으로 옮겼다.
   - 팀 탭 → **소유자(owner) 탭**. GitHub 팀은 GitHub 만의 축이고 코어는 Gitea 다 — 어느 호스트에나 있는 owner 로 묶는다.
     「미등록」 → **프로젝트 없음**(어느 프로젝트에도 안 묶인 리포).
   - 검색 → 이름 · 주소(clone · web).  소스 → **연결**(어느 git 연결로 읽나, 설치 기본 포함).
   - 관리 대상 · 아카이브 · 사라진 리포 · 토픽 · 헬스는 b3x-in 리포에 그 값이 없다 — 지어내지 않는다(pr-port.md §10).
   거르개는 **화면 거르개**다 — `GET /repos` 는 쪽 없이 전부 온다. */

export type RepoLike = {
  id: string;
  owner: string;
  name: string;
  cloneUrl: string;
  webUrl: string | null;
  connectionId: string | null;
  projectIds?: string[];
};

/** 탭 값: `""` 전체 · `"\u0000none"` 프로젝트 없음 · 그 밖은 owner 이름. owner 와 안 겹치게 NUL 을 붙였다. */
export const TAB_NONE = "\u0000none";
/** 연결 값: `""` 전체 · `"\u0000legacy"` 설치 기본 · 그 밖은 연결 id. */
export const CONN_LEGACY = "\u0000legacy";

export type RepoFilter = { tab: string; q: string; conn: string };
export const EMPTY_FILTER: RepoFilter = { tab: "", q: "", conn: "" };

/** 거르기 — 순수 함수. 탭 · 검색 · 연결 셋을 다 통과한 줄만 남긴다. */
export function filterRepos<T extends RepoLike>(rows: T[], f: RepoFilter): T[] {
  const q = f.q.trim().toLowerCase();
  return rows.filter((r) => {
    if (f.tab === TAB_NONE ? (r.projectIds ?? []).length > 0 : f.tab && r.owner !== f.tab) return false;
    if (f.conn === CONN_LEGACY ? r.connectionId !== null : f.conn && r.connectionId !== f.conn) return false;
    if (q && ![`${r.owner}/${r.name}`, r.cloneUrl, r.webUrl ?? ""].some((s) => s.toLowerCase().includes(q))) return false;
    return true;
  });
}

/** owner 탭 — 수가 많은 순. 탭 수는 **검색 · 연결을 건 뒤가 아니라 전체**에서 센다(탭이 사라지지 않게). */
export function ownerTabs(rows: RepoLike[]): { owner: string; n: number }[] {
  const m = new Map<string, number>();
  for (const r of rows) m.set(r.owner, (m.get(r.owner) ?? 0) + 1);
  return [...m].map(([owner, n]) => ({ owner, n })).sort((a, b) => b.n - a.n || a.owner.localeCompare(b.owner));
}

export function RepoFilterBar({
  rows,
  value,
  conns,
  shown,
  onChange,
}: {
  rows: RepoLike[];
  value: RepoFilter;
  /** 고를 수 있는 연결 — 이름표 그대로. */
  conns: { id: string; label: string }[];
  /** 거른 뒤 남은 수. */
  shown: number;
  onChange: (next: RepoFilter) => void;
}) {
  const tabs = ownerTabs(rows);
  const none = rows.filter((r) => !(r.projectIds ?? []).length).length;
  const tab = (key: string, label: string, n: number) => (
    <button
      key={key}
      type="button"
      role="tab"
      aria-selected={value.tab === key}
      className={`b-tab${value.tab === key ? " is-on" : ""}`}
      onClick={() => onChange({ ...value, tab: key })}
    >
      {label} <span className="b-tab-count">{n}</span>
    </button>
  );
  return (
    <div className="rfilter">
      <div className="rfilter__tabs">
        <div className="b-tabs" role="tablist" aria-label={tx("repoFilter.owner")}>
          {tab("", tx("repoFilter.all"), rows.length)}
          {tabs.map((t) => tab(t.owner, t.owner, t.n))}
          {tab(TAB_NONE, tx("repoFilter.noProject"), none)}
        </div>
      </div>
      <div className="b-filterbar">
        <div className="b-filterbar-search">
          <Input size="md" type="search" value={value.q} placeholder={tx("repoFilter.search")} aria-label={tx("repoFilter.search")} onChange={(e) => onChange({ ...value, q: e.target.value })} />
        </div>
        <div className="b-filterbar-chips">
          <Select size="md" value={value.conn} aria-label={tx("repoFilter.conn")} onChange={(e) => onChange({ ...value, conn: e.target.value })}>
            <option value="">{tx("repoFilter.connAll")}</option>
            {conns.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
            <option value={CONN_LEGACY}>{tx("gitConn.pickLegacy")}</option>
          </Select>
        </div>
        <div className="b-filterbar-end">
          <span className="b-filterbar-count">{tx("repoFilter.count", { n: shown, all: rows.length })}</span>
          {value.tab || value.q || value.conn ? (
            <Button size="sm" variant="ghost" onClick={() => onChange(EMPTY_FILTER)}>
              {tx("repoFilter.reset")}
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
